// frontend/src/components/ProtectedAdminRoute.jsx
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Container, CircularProgress, Alert } from '@mui/material';
import { checkSuperAdminAccess } from '../services/adminService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Wrapper component that protects admin routes
 * Only allows access to super admins (rich@bidtransparency.com)
 */
const ProtectedAdminRoute = ({ children }) => {
    const { isAuthenticated, user } = useAuth();
    const [isCheckingAccess, setIsCheckingAccess] = useState(true);
    const [hasAccess, setHasAccess] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const verifyAccess = async () => {
            // First check if user is authenticated
            if (!isAuthenticated || !user) {
                setIsCheckingAccess(false);
                return;
            }

            try {
                const accessGranted = await checkSuperAdminAccess();
                setHasAccess(accessGranted);
                setError(null);
            } catch (err) {
                console.error('Error checking super admin access:', err);
                setHasAccess(false);
                setError('Unable to verify admin privileges');
            } finally {
                setIsCheckingAccess(false);
            }
        };

        verifyAccess();
    }, [isAuthenticated, user]);

    // Show loading while checking access
    if (isCheckingAccess) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Show access denied if not super admin
    if (!hasAccess) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4 }}>
                <Alert severity="error">
                    Access denied. You do not have administrative privileges.
                    {error && ` (${error})`}
                </Alert>
            </Container>
        );
    }

    // Render admin content if access is granted
    return children;
};

export default ProtectedAdminRoute;